import { createContext, useContext, useMemo } from 'react';
import { FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form';
import * as Slider from '@givebutter/primitives/slider';
import { useGivebutterFormContext } from './useGivebutterFormContext';
import { twMerge } from 'tailwind-merge';

interface IUseFormSliderContext<T extends FieldValues, K extends FieldPath<T>>
    extends UseControllerProps<T, K> {}

const UseFormSliderContext = createContext<IUseFormSliderContext<any, any>>({
    name: '',
});

interface UseFormSliderRootProps<T extends FieldValues, K extends FieldPath<T>>
    extends UseControllerProps<T, K>,
        Omit<Slider.SliderRootProps, 'defaultValue' | 'name'> {}

const UseFormSliderRoot = <T extends FieldValues, K extends FieldPath<T>>({
    children,
    className,
    ...controllerProps
}: UseFormSliderRootProps<T, K>) => {
    const ctx = useMemo(() => controllerProps, [controllerProps]);

    return (
        <UseFormSliderContext.Provider value={ctx}>
            <div className={twMerge('flex flex-col', className)}>
                <div className="flex items-center justify-start gap-6">{children}</div>
            </div>
        </UseFormSliderContext.Provider>
    );
};

interface UseFormSliderProps extends Slider.SliderRootProps, React.RefAttributes<HTMLSpanElement> {}

const UseFormSlider = ({
    children,
    defaultValue,
    onValueChange,
    ...restConsumerProps
}: UseFormSliderProps) => {
    const controllerProps = useContext(UseFormSliderContext);

    const {
        field: { onChange: onChangeInternal, value, ref: formRef, ...restFormProps },
    } = useController(controllerProps);

    const onChange = (value: number[]) => {
        onValueChange?.(value);
        onChangeInternal(value);
    };

    return (
        <Slider.Root
            value={value}
            defaultValue={defaultValue}
            onValueChange={onChange}
            {...restFormProps}
            {...restConsumerProps}
        >
            <Slider.Track>
                <Slider.Range />
            </Slider.Track>
            {children}
        </Slider.Root>
    );
};

interface UseFormSliderInputProps extends Slider.SliderInputProps {}

const UseFormSliderInput = (props: UseFormSliderInputProps) => {
    const { name } = useContext(UseFormSliderContext);
    const { watch } = useGivebutterFormContext();
    const value = watch(name);

    return <Slider.SliderInput value={value} {...props} />;
};

const UseFormSliderThumb = Slider.Thumb;

export { UseFormSlider, UseFormSliderRoot, UseFormSliderInput, UseFormSliderThumb };
export type { UseFormSliderProps, UseFormSliderRootProps, UseFormSliderInputProps };
